// White-label configuration consumed from Vite env vars.
//
// Values originate in `.env` (declared in `.env.example`) and are
// inlined into the client bundle at build time. `applyBranding()`
// must run before React renders so the document title, theme-color
// meta, and `--color-brand` CSS variable reflect the deployed tenant's
// brand rather than the fallback values emitted by `src/index.css`.

const DEFAULT_APP_NAME = 'Citycare Property Asset Capture'

export interface Brand {
  readonly appName: string
  readonly logoUrl: string | undefined
  readonly primaryColor: string | undefined
}

export function readBrand(env: ImportMetaEnv = import.meta.env): Brand {
  return {
    appName: nonEmpty(env.VITE_APP_NAME) ?? DEFAULT_APP_NAME,
    logoUrl: nonEmpty(env.VITE_LOGO_URL),
    primaryColor: nonEmpty(env.VITE_PRIMARY_COLOR),
  }
}

/**
 * Derives a readable foreground colour for a given hex background.
 *
 * Uses the WCAG relative-luminance formula to choose between a dark or light
 * foreground. Returns `--color-ink` (Firefly) for light backgrounds and
 * `--color-surface` (Spring Wood) for dark backgrounds, expressed as CSS
 * custom-property references so they stay in sync with the token sheet.
 *
 * @param hex - A CSS hex colour string, e.g. the value of VITE_PRIMARY_COLOR.
 * @returns A CSS value string suitable for `setProperty`.
 */
export function deriveForeground(hex: string): string {
  const lum = relativeLuminance(hex)
  // WCAG contrast threshold: luminance > 0.179 → dark text reads better.
  return lum > 0.179 ? 'var(--color-ink)' : 'var(--color-surface)'
}

/**
 * Token references retained here so `@tailwindcss/vite` content scanning
 * picks up every brand token and emits it in the :root block even before
 * Wave 2/3 components adopt the new utilities.
 *
 * bg-brand text-brand-foreground
 * bg-brand-deep text-brand-deep-foreground
 * bg-surface text-surface
 * bg-ink text-ink
 */

export function applyBranding(
  brand: Brand = readBrand(),
  doc: Document = document,
): void {
  doc.title = brand.appName

  if (brand.primaryColor !== undefined) {
    doc.documentElement.style.setProperty('--color-brand', brand.primaryColor)
    doc.documentElement.style.setProperty(
      '--color-brand-foreground',
      deriveForeground(brand.primaryColor),
    )
    setMetaContent(doc, 'theme-color', brand.primaryColor)
  }
}

function nonEmpty(value: string | undefined): string | undefined {
  if (value === undefined) return undefined
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

/**
 * Parses a CSS hex colour string and returns its WCAG relative luminance
 * (0 = black, 1 = white). Supports 3-digit and 6-digit forms.
 * Returns 0 (treat as dark) for any unparseable input.
 */
function relativeLuminance(hex: string): number {
  const clean = hex.replace(/^#/, '')
  let r: number, g: number, b: number

  if (clean.length === 3) {
    const r0 = clean[0] ?? '0'
    const g0 = clean[1] ?? '0'
    const b0 = clean[2] ?? '0'
    r = parseInt(r0 + r0, 16)
    g = parseInt(g0 + g0, 16)
    b = parseInt(b0 + b0, 16)
  } else if (clean.length === 6) {
    r = parseInt(clean.slice(0, 2), 16)
    g = parseInt(clean.slice(2, 4), 16)
    b = parseInt(clean.slice(4, 6), 16)
  } else {
    return 0
  }

  const toLinear = (c: number): number => {
    const s = c / 255
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
}

function setMetaContent(doc: Document, name: string, content: string): void {
  let meta = doc.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (meta === null) {
    meta = doc.createElement('meta')
    meta.setAttribute('name', name)
    doc.head.appendChild(meta)
  }
  meta.setAttribute('content', content)
}
